/**
 * Web Push subscription lifecycle for the master frontend.
 *
 * Mirrors the subscription logic in packages/frontend/src/services/notification.service.ts
 * for the master origin. Because the master app may run on its own subdomain,
 * its service-worker scope and IndexedDB are origin-isolated from the tenant
 * frontend — subscriptions must be created and managed independently here.
 *
 * Design decisions:
 *  - Never auto-prompts for Notification permission; only subscribes when
 *    permission was already granted by the user in a previous session.
 *  - Uses the same backend endpoints as the tenant frontend
 *    (GET /notifications/push/vapid-key, POST /push/subscribe, POST /push/unsubscribe)
 *    because master users have a real tenant context (the master tenant).
 *  - Stores a lightweight "subscribed" flag in localStorage under
 *    MASTER_PUSH_SUBSCRIPTION_KEY so the init flow avoids redundant
 *    PushManager.getSubscription() calls on every page load.
 */

import { apiClient } from '@/lib/api';

const MASTER_PUSH_SUBSCRIPTION_KEY = 'edgvoip_master_push_subscription';

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    // apiClient.get() returns the full backend envelope:
    // { success: boolean, data: { vapidPublicKey: string }, timestamp: string }
    // so the key lives at res.data.vapidPublicKey (not res.data.data.vapidPublicKey).
    const res = await apiClient.get<{ vapidPublicKey: string }>(
      '/notifications/push/vapid-key'
    );
    return res.data?.vapidPublicKey ?? null;
  } catch {
    return null;
  }
}

async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
  const data = subscription.toJSON();
  await apiClient.post('/notifications/push/subscribe', {
    subscription: {
      endpoint: data.endpoint,
      keys: {
        p256dh: data.keys?.p256dh,
        auth: data.keys?.auth,
      },
    },
  });
}

/**
 * Initialise the master Web Push subscription.
 *
 * Call after the master session is restored or after a successful login.
 * Only creates a new subscription when:
 *  - The browser supports PushManager
 *  - Notification permission is already 'granted' (no silent prompting)
 *  - The SW is ready
 *
 * On success the subscription endpoint is (re-)registered with the backend so
 * the server knows which key to use when sending pushes to this device.
 * Non-fatal: failures are logged and swallowed.
 */
export async function initMasterPushSubscription(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const vapidPublicKey = await fetchVapidPublicKey();
    if (!vapidPublicKey) return;

    const registration = await navigator.serviceWorker.ready;

    // Re-use an existing subscription rather than creating a new one.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await sendSubscriptionToServer(existing);
      localStorage.setItem(MASTER_PUSH_SUBSCRIPTION_KEY, 'true');
      return;
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    await sendSubscriptionToServer(subscription);
    localStorage.setItem(MASTER_PUSH_SUBSCRIPTION_KEY, 'true');
  } catch (err) {
    // Non-fatal: push is a best-effort channel. Log so ops can diagnose.
    console.warn('[EasyFlow Master Push] Subscription init failed:', (err as Error)?.message ?? err);
  }
}

/**
 * Remove the master Web Push subscription on logout.
 *
 * Notifies the backend so it stops sending pushes to this device and
 * unsubscribes from the browser's PushManager. Non-fatal.
 */
export async function unsubscribeFromMasterPush(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      try {
        await apiClient.post('/notifications/push/unsubscribe', {
          endpoint: subscription.endpoint,
        });
      } catch {
        /* ignore network errors during logout */
      }
      await subscription.unsubscribe();
    }

    localStorage.removeItem(MASTER_PUSH_SUBSCRIPTION_KEY);
  } catch (err) {
    console.warn('[EasyFlow Master Push] Unsubscribe failed:', (err as Error)?.message ?? err);
  }
}
